import AddIcon from '@mui/icons-material/Add'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import InfoIcon from '@mui/icons-material/Info'
import { Box, CircularProgress, IconButton, TextField, Tooltip, Typography } from '@mui/material'
import { usePostMerchantReferenceCode } from '@src/data/api/credit-api/credit-api'
import { CreditApplication } from '@src/types/CreditApplicationSchema'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  creditApp: CreditApplication
}

export const MerchantReferenceCodeField = ({ creditApp }: Props) => {
  const { t } = useTranslation()
  const [postMerchantReferenceCode, isPending, isError, error] = usePostMerchantReferenceCode()

  const [merchantReferenceCode, setMerchantReferenceCode] = useState<string | null>(
    creditApp.merchantReferenceCode || null,
  )
  const [isEditing, setIsEditing] = useState(false)
  const [originalMerchantReferenceCode, setOriginalMerchantReferenceCode] = useState<string | null>(null)

  const handleMerchantReferenceCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value
    if (input.length <= 20) {
      setMerchantReferenceCode(input)
    }
  }

  const handleStartEditing = () => {
    setOriginalMerchantReferenceCode(merchantReferenceCode)
    setIsEditing(true)
  }

  const handleSave = async () => {
    if (merchantReferenceCode !== creditApp.merchantReferenceCode) {
      try {
        await postMerchantReferenceCode({
          creditApplicationId: creditApp.id!,
          financingProgramId: creditApp.financingProgramId,
          merchantReferenceCodeDto: {
            merchantReferenceCode: merchantReferenceCode || '',
            versionTag: creditApp.versionTag || '',
          },
        })
      } catch (error) {
        console.error('Error saving merchant reference code:', error)
        return
      }
    }
    setIsEditing(false)
  }

  const handleCancel = () => {
    setMerchantReferenceCode(originalMerchantReferenceCode)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography variant="subtitle1"> {t('applicationHeader.yourReferenceCode')} </Typography>
        <Tooltip title={t('applicationHeader.tooltips.merchantReferenceCodeInfo')}>
          <InfoIcon sx={{ fontSize: 'medium', cursor: 'pointer', placement: 'bottom' }} />
        </Tooltip>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          id="merchant-reference-code"
          value={
            isEditing
              ? merchantReferenceCode || ''
              : merchantReferenceCode || t('applicationHeader.tooltips.addMerchantReferenceCode')
          }
          onChange={handleMerchantReferenceCodeChange}
          onKeyDown={handleKeyDown}
          onClick={!isEditing ? handleStartEditing : undefined}
          helperText={isEditing ? t('applicationHeader.tooltips.maxCharacters', { max: 20 }) : undefined}
          autoFocus={isEditing}
          fullWidth
          size="small"
          disabled={isPending}
          slotProps={{
            htmlInput: { maxLength: 20 },
            input: {
              readOnly: !isEditing,
            },
          }}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'start',
            height: '100%',
            alignSelf: 'start',
          }}
        >
          {isPending ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }}>
              <CircularProgress size={16} />
            </Box>
          ) : isEditing ? (
            <>
              <IconButton onClick={handleSave} size="small" color="success" disabled={isPending}>
                <CheckIcon />
              </IconButton>
              <IconButton onClick={handleCancel} size="small" color="error" disabled={isPending}>
                <CloseIcon />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={handleStartEditing} size="small" color="primary">
              {merchantReferenceCode ? <EditIcon /> : <AddIcon />}
            </IconButton>
          )}
        </Box>
      </Box>
      {isError && (
        <Typography variant="caption" color="error">
          {error?.name || 'Error saving note'}
        </Typography>
      )}
    </Box>
  )
}
